import type { StageStatus, TodayVisit } from "@visits/visitsClient";

/**
 * One calendar block. A household with several patients booked into the exact
 * same slot is represented by one block so a litter does not turn into a row of
 * unreadably narrow, duplicate appointments (WEB-3219).
 */
export interface CalendarVisitGroup {
  clientId: string | null;
  clientName: string | null;
  visits: TodayVisit[]; // always length >= 1
}

const slotKey = (visit: TodayVisit): string | null => {
  if (!visit.clientId || !visit.scheduledStart) return null;

  // End time is part of the slot: visits that merely start together but are
  // separate appointments should retain their own calendar geometry.
  return JSON.stringify([
    visit.clientId,
    visit.scheduledStart,
    visit.scheduledEnd,
  ]);
};

/**
 * Collapse visits that share both a client id and scheduled slot. Names are not
 * identity-safe, and visits without a client id remain separate. Input order is
 * preserved, with each group anchored at its first visit.
 */
export const groupCalendarVisits = (
  visits: TodayVisit[],
): CalendarVisitGroup[] => {
  const visitsBySlot = new Map<string, TodayVisit[]>();
  for (const visit of visits) {
    const key = slotKey(visit);
    if (!key) continue;

    const bucket = visitsBySlot.get(key);
    if (bucket) bucket.push(visit);
    else visitsBySlot.set(key, [visit]);
  }

  const emittedSlots = new Set<string>();
  const groups: CalendarVisitGroup[] = [];
  for (const visit of visits) {
    const key = slotKey(visit);
    if (!key) {
      groups.push({
        clientId: visit.clientId,
        clientName: visit.clientName,
        visits: [visit],
      });
      continue;
    }

    if (emittedSlots.has(key)) continue;
    emittedSlots.add(key);
    groups.push({
      clientId: visit.clientId,
      clientName: visit.clientName,
      visits: visitsBySlot.get(key)!,
    });
  }

  return groups;
};

const MULTI_PATIENT_LABEL = "Multi-patient appointment";

/** The block's aggregate visual + textual state, derived from its group. */
export interface CalendarGroupSummary {
  /** More than one patient collapsed into this block. */
  isMultiPatient: boolean;
  /** True only when every visit in the group is cancelled. */
  cancelled: boolean;
  /** Highest-priority stage across the group: active outranks done. */
  accentStatus: StageStatus | undefined;
  /**
   * Title for a multi-patient block ("Client name (N)", or the generic phrase
   * when the client is unknown). null for a single visit, where the caller
   * renders the patient/client name instead.
   */
  title: string | null;
  /**
   * Secondary line for a multi-patient block. null when it would merely repeat
   * the title (unknown client) or for a single visit, where the caller falls
   * back to the visit's reason/type.
   */
  subtitle: string | null;
}

/**
 * Collapse a group's per-visit state into the one thing the block shows. A
 * household reads as cancelled only if all its visits are, and takes the most
 * advanced stage accent present (active over done). Pure, so the aggregation
 * rules are unit-testable without rendering.
 */
export const summarizeCalendarGroup = (
  group: CalendarVisitGroup,
): CalendarGroupSummary => {
  const isMultiPatient = group.visits.length > 1;
  const accentStatus: StageStatus | undefined = group.visits.some(
    (visit) => visit.currentStage?.status === "active",
  )
    ? "active"
    : group.visits.some((visit) => visit.currentStage?.status === "done")
      ? "done"
      : undefined;

  return {
    isMultiPatient,
    // Cancelled only when every visit is. Mirrors isVisitCancelled (a
    // `cancelledAt` timestamp), inlined so this layout helper stays a pure leaf
    // rather than pulling in the API-client import chain from visitsClient.
    cancelled: group.visits.every((visit) => visit.cancelledAt !== null),
    accentStatus,
    title: isMultiPatient
      ? `${group.clientName ?? MULTI_PATIENT_LABEL} (${group.visits.length})`
      : null,
    // When the client is known the title carries the name, so the generic
    // phrase adds context; when it isn't, the title already is that phrase.
    subtitle: isMultiPatient && group.clientName ? MULTI_PATIENT_LABEL : null,
  };
};
